import { useState } from 'react';
import { facilityStaffCredential, type ApiError, type StaffCredential, type StaffCredentialType } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ErrLine, I, btn, chip, sx, useGuarded } from './kit';

const INPUT = 'box-sizing:border-box;height:30px;padding:0 9px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;color:#22272E;';
const CAPTION = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:6px;';

/** The credential types a person can hold on file (the server's STORED_CREDENTIAL_TYPES). */
export const CREDENTIAL_OPTIONS: ReadonlyArray<{ value: StaffCredentialType; label: string }> = [
  { value: 'rn', label: 'Registered nurse (RN)' },
  { value: 'lpn_lvn', label: 'LPN / LVN' },
  { value: 'medication_aide_permit', label: 'Medication aide (permit)' },
  { value: 'rn_delegated_aide', label: 'Aide with RN delegation' },
  { value: 'cna', label: 'Nurse aide (CNA)' },
];
const VERIFY_LABEL: Record<StaffCredential['verification'], string> = {
  unverified: 'Not verified',
  verified_manually: 'Verified by hand',
  verified_nursys: 'Verified with Nursys',
};

/** One line about a credential, for the staff row and the editor. */
export function credentialLine(c: StaffCredential | null | undefined): string {
  if (!c) return 'No license or permit on file · only a licensed nurse records a dose where no rules are loaded';
  const opt = CREDENTIAL_OPTIONS.find((o) => o.value === c.type)?.label ?? c.label;
  return [opt, c.licenseNumber ? '#' + c.licenseNumber : null, c.issuingJurisdiction + (c.multistate ? ' (multistate)' : ''), 'expires ' + (c.expiresOn ?? 'unknown'), VERIFY_LABEL[c.verification]]
    .filter(Boolean).join(' · ');
}

/** The chip on a staff row: what their credential counts as here. */
export function CredentialChip({ c }: { c: StaffCredential | null | undefined }) {
  if (!c) return null;
  const counts = c.countsAs !== 'none';
  const short: Record<StaffCredentialType, string> = { rn: 'RN', lpn_lvn: 'LPN/LVN', medication_aide_permit: 'MED AIDE', rn_delegated_aide: 'RN-DELEGATED', cna: 'CNA' };
  return (
    <span data-testid={'cred-chip-' + c.id} title={c.notCounted ?? credentialLine(c)} style={sx(counts ? chip('#12503C', 'rgba(47,138,111,.16)') : chip('#B3261E', 'rgba(179,38,30,.12)'))}>
      {I(counts ? short[c.type] : short[c.type] + ' EXPIRED OR NOT VALID HERE')}
    </span>
  );
}

/**
 * A staff member's license or permit (PUT /facilities/me/staff/:id/credential), set by their
 * Facility Admin: type, number, issuing jurisdiction, expiry and how it was verified. It decides
 * which medicines they may record as given (who may give which medicine). Saving a new one keeps
 * the old one on file, retired; Remove retires it. Nursys verification is not offered: it comes
 * only from the Nursys check, once Aithora has that account.
 */
export function CredentialEditor({ userId, name, credential, onSaved }: { userId: string; name: string; credential: StaffCredential | null; onSaved: () => void }) {
  const { client } = useSession();
  const { wr } = useGuarded();
  const [form, setForm] = useState({
    type: (credential?.type ?? '') as StaffCredentialType | '',
    licenseNumber: credential?.licenseNumber ?? '',
    issuingJurisdiction: credential?.issuingJurisdiction ?? '',
    expiresOn: credential?.expiresOn ?? '',
    verified: credential?.verification === 'verified_manually',
    multistate: credential?.multistate ?? false,
  });
  const [err, setErr] = useState<ApiError | null>(null);
  const [saved, setSaved] = useState(false);
  const nurse = form.type === 'rn' || form.type === 'lpn_lvn';
  const ok = !!form.type && /^[A-Za-z]{2}(-[A-Za-z0-9]{1,3})?$/.test(form.issuingJurisdiction.trim()) && /^\d{4}-\d{2}-\d{2}$/.test(form.expiresOn);
  const save = async (clear: boolean) => {
    setErr(null);
    setSaved(false);
    try {
      await facilityStaffCredential(client, {
        params: { id: 'me', userId },
        body: clear ? { type: null } : {
          type: form.type as StaffCredentialType,
          licenseNumber: form.licenseNumber.trim() || null,
          issuingJurisdiction: form.issuingJurisdiction.trim().toUpperCase(),
          expiresOn: form.expiresOn,
          verification: form.verified ? 'verified_manually' : 'unverified',
          multistate: nurse && form.multistate,
        },
      });
      setSaved(true);
      onSaved();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  return (
    <div data-testid="credential-editor" style={sx('margin:0 0 14px;padding-bottom:12px;border-bottom:1px solid rgba(34,39,46,.1);')}>
      <div style={sx(CAPTION)}>Medication credential</div>
      <div style={sx('font-size:16px;margin-bottom:4px;')}>{I(credentialLine(credential))}</div>
      {credential?.notCounted && <div role="status" style={sx('font-size:16px;color:#B3261E;margin-bottom:6px;')}>{I(credential.notCounted)}</div>}
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:7px;margin-top:6px;')}>
        <select aria-label={`Credential type for ${name}`} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as StaffCredentialType | '' })} style={sx(INPUT + 'min-width:170px;')}>
          <option value="">Type</option>
          {CREDENTIAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input type="text" aria-label={`License or permit number for ${name}`} placeholder="License or permit number" value={form.licenseNumber} onChange={(e) => setForm({ ...form, licenseNumber: e.target.value.slice(0, 40) })} style={sx(INPUT + 'width:170px;')} />
        <input type="text" aria-label={`Issued by (state) for ${name}`} placeholder="Issued by, e.g. US-TX" value={form.issuingJurisdiction} onChange={(e) => setForm({ ...form, issuingJurisdiction: e.target.value.slice(0, 6) })} style={sx(INPUT + 'width:130px;')} />
        <input type="date" aria-label={`Expires on for ${name}`} value={form.expiresOn} onChange={(e) => setForm({ ...form, expiresOn: e.target.value })} style={sx(INPUT + 'width:150px;')} />
        <label style={sx('display:inline-flex;align-items:center;gap:5px;font-size:16px;color:#3C454F;')}>
          <input type="checkbox" checked={form.verified} onChange={(e) => setForm({ ...form, verified: e.target.checked })} />
          {I('I checked it with the issuing board')}
        </label>
        {nurse && (
          <label style={sx('display:inline-flex;align-items:center;gap:5px;font-size:16px;color:#3C454F;')}>
            <input type="checkbox" checked={form.multistate} onChange={(e) => setForm({ ...form, multistate: e.target.checked })} />
            {I('Multistate (nurse compact)')}
          </label>
        )}
      </div>
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:8px;')}>
        <a href="#" {...wr(() => { if (ok) void save(false); })} aria-disabled={!ok || undefined} style={sx(btn(ok ? 'primary' : 'off'))}>
          Save credential
        </a>
        {credential && (
          <a href="#" {...wr(() => void save(true))} style={sx(btn('ghost'))}>
            Remove
          </a>
        )}
        {saved && <span role="status" style={sx('font-size:16px;color:#1F6B52;')}>{I(credential ? 'Saved. Any earlier one stays on file, retired.' : 'Saved.')}</span>}
      </div>
      <ErrLine error={err} />
    </div>
  );
}
